import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Loader2, FileImage, Plus, Globe } from "lucide-react";
import { openBooking } from "@/lib/booking";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-300",
  confirmed: "bg-sky-50 text-sky-800 border-sky-300",
  completed: "bg-slate-100 text-slate-700 border-slate-300",
  delivered: "bg-emerald-50 text-emerald-800 border-emerald-300",
  cancelled: "bg-red-50 text-red-700 border-red-300",
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);

  useEffect(() => { api.get("/bookings/me").then((r) => setBookings(r.data)).catch(() => setBookings([])); }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-32 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-4 border-b border-border pb-8">
          <div>
            <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-500">My account</span>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-3">Welcome, {user?.name?.split(" ")[0]}</h1>
          </div>
          <Button data-testid="dashboard-new-booking" onClick={openBooking} className="rounded-none bg-slate-900 hover:bg-slate-800 text-slate-50">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> New booking
          </Button>
        </div>

        <div className="mt-12">
          {bookings === null ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading your jobs…</div>
          ) : bookings.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center">
              <FileImage className="h-10 w-10 mx-auto text-slate-300" strokeWidth={1.5} />
              <p className="text-slate-500 mt-4">No bookings yet.</p>
              <Button data-testid="dashboard-first-booking" onClick={openBooking} className="rounded-none bg-slate-900 text-slate-50 mt-6">Book your first shoot</Button>
            </div>
          ) : (
            <div className="border border-border divide-y divide-border" data-testid="client-bookings">
              {bookings.map((b) => (
                <div key={b.id} data-testid={`booking-${b.reference}`} className="p-6 bg-card hover:bg-accent/10 transition-colors">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm">{b.reference}</span>
                        <span className={`text-[11px] font-mono uppercase tracking-[0.1em] px-2.5 py-1 border ${STATUS_STYLES[b.status] || ""}`}>{b.status}</span>
                      </div>
                      <button data-testid={`booking-address-${b.reference}`} onClick={() => navigate(`/dashboard/jobs/${b.id}`)} className="flex items-center gap-2 text-sm text-slate-500 mt-3 hover:text-[#C7A361] transition-colors text-left"><MapPin className="h-4 w-4" strokeWidth={1.5} /> {b.address.street}, {b.address.city}</button>
                      {b.scheduled_date && <div className="flex items-center gap-2 text-sm text-slate-500 mt-1"><Calendar className="h-4 w-4" strokeWidth={1.5} /> {b.scheduled_date} at {b.scheduled_time}</div>}
                      <div className="text-sm text-slate-700 mt-3">{b.quote.line_items.map((l) => l.name).join(" · ")}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-4xl">${b.quote.total}</div>
                    </div>
                  </div>

                  <div className="mt-5 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-3">
                    <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">{b.files?.length ? `${b.files.length} file${b.files.length === 1 ? "" : "s"} delivered` : "No media delivered yet"}</span>
                    <Button data-testid={`open-job-${b.reference}`} variant="outline" onClick={() => navigate(`/dashboard/jobs/${b.id}`)} className="rounded-none border-slate-400 font-mono text-[11px] uppercase tracking-[0.1em]">
                      <Globe className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} /> Edit Property Website
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
